import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import FloatingContactButtons from '../../components/FloatingContactButtons';
import CallMeBackPopup from '../../components/CallMeBackPopup';
import { forceUrbaniaRoutes } from '../../data/forceUrbaniaRoutes';
import ContactSection from '../../components/ContactSection';
import { calculateOneWayFare, VEHICLE_RATES_2026 } from '../../utils/pricingCalculator';

const ForceUrbaniaRouteBooking = () => {
  const { route } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCallMeBack, setShowCallMeBack] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedDay, setSelectedDay] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);

  // Extract route key from URL path
  const locationPath = window.location?.pathname;
  const routeKey = route || locationPath?.split('/force-urbania-booking/')?.[1];

  // Check if route exists in predefined data, otherwise use dynamic calculation
  let routeData = forceUrbaniaRoutes?.[routeKey];

  // If route not found in predefined data, create dynamic route data from location state
  if (!routeData && location?.state?.from && location?.state?.to) {
    const from = location?.state?.from;
    const to = location?.state?.to;

    // Estimate distance and toll (you can enhance this with actual distance API)
    const estimatedDistance = 200; // Default estimate
    const estimatedToll = 280;

    routeData = {
      from,
      to,
      fareStart: calculateOneWayFare(estimatedDistance, VEHICLE_RATES_2026?.urbania12, estimatedToll),
      vehicles: [
      {
        id: 1,
        name: 'Urbania',
        seats: 12,
        luggage: 9,
        price: calculateOneWayFare(estimatedDistance, VEHICLE_RATES_2026?.urbania12, estimatedToll),
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_16387bc8f-1768524310886.png",
        imageAlt: 'Force Urbania 12 seater'
      },
      {
        id: 2,
        name: 'Force Urbania',
        seats: 16,
        luggage: 16,
        price: calculateOneWayFare(estimatedDistance, VEHICLE_RATES_2026?.urbania16, estimatedToll),
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_1364d5d99-1768517529483.png",
        imageAlt: 'Force Urbania 16 seater'
      }]

    };
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!routeData) {
      navigate('/force-urbania');
    } else if (routeData?.dayOptions && routeData?.dayOptions?.length > 0) {
      setSelectedDay(routeData?.dayOptions?.[0]);
    }
  }, [routeData, navigate]);

  if (!routeData) {
    return null;
  }

  const handleBookNow = (vehicle) => {
    navigate('/car-details', {
      state: {
        from: routeData?.from,
        to: routeData?.to,
        vehicle: vehicle?.name,
        price: vehicle?.price,
        seats: vehicle?.seats,
        luggage: vehicle?.luggage,
        image: vehicle?.image,
        alt: vehicle?.imageAlt
      }
    });
  };

  const faqs = [
  {
    question: 'How many passengers can sit in Force Urbania?',
    answer: 'Force Urbania is available in two variants: 12-seater and 16-seater configurations. Both variants offer comfortable seating with ample legroom and modern amenities for a pleasant journey.'
  },
  {
    question: 'Can I book the Force Urbania for short trips?',
    answer: 'Yes, you can book Force Urbania for short trips. We offer flexible booking options ranging from 3 days to 8 days, allowing you to choose the duration that best suits your travel needs.'
  },
  {
    question: 'Can I use the Force Urbania for long distance travel?',
    answer: 'Absolutely! Force Urbania is perfect for long distance travel. Our vehicles are well-maintained, comfortable, and equipped with modern amenities to ensure a smooth journey across long distances.'
  },
  {
    question: 'Does the Force Urbania have an air condition facility?',
    answer: 'Yes, all our Force Urbania vehicles come with fully functional air conditioning systems to ensure your comfort throughout the journey, regardless of weather conditions.'
  },
  {
    question: 'What amenities are included with the Force Urbania?',
    answer: 'Our Force Urbania vehicles include air conditioning, comfortable push-back seats, ample luggage space, music system, charging points, and first aid kit. Premium variants may include additional features like LED TV and premium interiors.'
  },
  {
    question: 'How can I book the Force Urbania services with a driver?',
    answer: 'You can book Force Urbania with a driver by clicking the "Select Car" button on this page, or by calling us at +91-79986-66660. Our team will assist you with the booking process and answer any questions you may have.'
  },
  {
    question: 'Is driver facility included in Force Urbania rental service?',
    answer: 'Yes, all Force Urbania rentals include an experienced and professional driver. Driver charges, including their accommodation and meals during the trip, are included in the quoted price.'
  },
  {
    question: 'What is the cancellation policy for Force Urbania rental?',
    answer: 'Our cancellation policy allows free cancellation up to 48 hours before the scheduled departure. Cancellations made within 48 hours may incur a nominal charge. Please contact our customer service for detailed cancellation terms.'
  },
  {
    question: 'Are there mileage limits on the rentals?',
    answer: 'Our Force Urbania rentals typically include unlimited mileage for the booked duration. However, specific terms may vary based on the package selected. Please confirm mileage details at the time of booking.'
  },
  {
    question: 'How often are the Force Urbania vehicles serviced?',
    answer: 'All our Force Urbania vehicles undergo regular maintenance and servicing as per manufacturer guidelines. Each vehicle is thoroughly inspected before every trip to ensure safety and reliability.'
  },
  {
    question: 'Can I rent the Force Urbania for corporate events?',
    answer: 'Yes, Force Urbania is an excellent choice for corporate events, team outings, and business travel. We offer special corporate packages with flexible billing options. Contact us for customized corporate travel solutions.'
  },
  {
    question: 'Are there discounts for long term rentals?',
    answer: 'Yes, we offer attractive discounts for long-term rentals and multi-day bookings. The longer your rental period, the better rates you can enjoy. Contact our team to discuss special pricing for extended rentals.'
  },
  {
    question: `What is the cost of Force Urbania rent from ${routeData?.from} to ${routeData?.to}?`,
    answer: `The cost of Force Urbania from ${routeData?.from} to ${routeData?.to} starts at ₹${routeData?.fareStart?.toLocaleString('en-IN')}/- for the 12-seater variant. The 16-seater premium variant is available at ₹${routeData?.vehicles?.[1]?.price?.toLocaleString('en-IN')}/-. Prices include driver charges, fuel, and all taxes.`
  }];


  return (
    <>
      <Helmet>
        <title>{`Force Urbania from ${routeData?.from} to ${routeData?.to} | Advait Tour and Travels`}</title>
        <meta name="description" content={`Book Force Urbania from ${routeData?.from} to ${routeData?.to}. Starting at ₹${routeData?.fareStart?.toLocaleString('en-IN')}.`} />
      </Helmet>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        
        <main className="flex-grow">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-400 text-white py-16">
            <div className="container mx-auto px-4">
              <h1 className="text-4xl md:text-5xl font-bold text-center mb-4">
                {routeData?.from} to {routeData?.to} Force Urbania
              </h1>
            </div>
          </div>

          {/* Day Selection Tabs */}
          {routeData?.dayOptions && routeData?.dayOptions?.length > 0 &&
          <div className="bg-white shadow-sm">
              <div className="container mx-auto px-4 py-6">
                <div className="flex flex-wrap gap-3 justify-center">
                  {routeData?.dayOptions?.map((day) =>
                <button
                  key={day}
                  onClick={() => setSelectedDay(day)}
                  className={`px-6 md:px-8 py-3 rounded-full font-semibold text-sm md:text-base transition-all duration-200 ${
                  selectedDay === day ?
                  'bg-yellow-400 text-gray-900 shadow-md' :
                  'bg-white text-gray-700 border border-gray-300 hover:border-yellow-400'}`
                  }>

                      {day}
                    </button>
                )}
                </div>
              </div>
            </div>
          }

          {/* Vehicle Selection */}
          <div className="container mx-auto px-4 py-12">
            <div className="space-y-6 max-w-5xl mx-auto">
              {routeData?.vehicles?.map((vehicle) =>
              <div
                key={vehicle?.id}
                className={`bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all cursor-pointer border-2 ${
                selectedVehicle?.id === vehicle?.id ? 'border-blue-500' : 'border-transparent'}`
                }
                onClick={() => setSelectedVehicle(vehicle)}>

                  <div className="flex flex-col md:flex-row">
                    {/* Vehicle Image */}
                    <div className="md:w-64 bg-gray-50 flex items-center justify-center p-6">
                      <img
                      src={vehicle?.image}
                      alt={vehicle?.imageAlt}
                      className="w-full h-auto object-contain" />

                    </div>
                    
                    {/* Vehicle Details */}
                    <div className="flex-1 p-6 flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="flex-1">
                        <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">CAR TYPE</p>
                        <h3 className="text-2xl font-bold text-gray-900 mb-1">
                          {vehicle?.name}
                        </h3>
                        <button className="text-green-600 font-semibold text-sm hover:underline mb-4">
                          More Details
                        </button>
                        
                        <div className="flex items-center gap-8">
                          <div className="flex items-center text-gray-700">
                            <svg className="w-6 h-6 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20 6h-3V4c0-1.11-.89-2-2-2H9c-1.11 0-2 .89-2 2v2H4c-1.11 0-2 .89-2 2v11c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zM9 4h6v2H9V4zm11 15H4V8h16v11z" />
                            </svg>
                            <div>
                              <p className="text-lg font-semibold">{vehicle?.luggage} Luggage</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center text-gray-700">
                            <svg className="w-6 h-6 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                            </svg>
                            <div>
                              <p className="text-lg font-semibold">{vehicle?.seats} Seats</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Price and CTA */}
                      <div className="mt-6 md:mt-0 md:ml-8 flex flex-col items-end justify-center">
                        <p className="text-3xl font-bold text-green-600 mb-1">
                          ₹{vehicle?.price?.toLocaleString('en-IN')}/-
                        </p>
                        <p className="text-sm text-gray-500 mb-4">Inc. of Taxes*</p>
                        
                        <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleBookNow(vehicle);
                        }}
                        className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold py-3 px-8 rounded-lg transition-colors shadow-md">

                          Select Car
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Features Section */}
          <div className="bg-white py-12">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
                Why Choose Our Force Urbania Service?
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                <div className="text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Well Maintained</h3>
                  <p className="text-gray-600">All vehicles are regularly serviced and maintained for your safety</p>
                </div>
                
                <div className="text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Experienced Drivers</h3>
                  <p className="text-gray-600">Professional and courteous drivers with years of experience</p>
                </div>
                
                <div className="text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Transparent Pricing</h3>
                  <p className="text-gray-600">No hidden charges - all taxes and tolls included</p>
                </div>
              </div>
            </div>
          </div>

          {/* Visitor's Guide Section */}
          <div className="bg-white py-16">
            <div className="container mx-auto px-4">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                Visitor's Guide
              </h2>
              
              <div className="max-w-4xl mx-auto space-y-8 text-gray-700 leading-relaxed">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Welcome to Advait Tour and Travels: Your Premier Choice for Force Urbania!
                  </h3>
                  <p className="text-lg">
                    Welcome to Advait Tour and Travels, where we transform your travel dreams into reality with our exceptional Force Urbania tour. Whether you're planning a family vacation, a corporate retreat, or a leisurely getaway with friends, our fleet of luxurious and comfortable Force Urbania vehicles is at your service. Discover why we are the go-to travel partner for those seeking an unparalleled travel experience from {routeData?.from} to the breathtaking landscapes of {routeData?.to}.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Explore {routeData?.to} with Ease and Comfort
                  </h3>
                  <p className="text-lg">
                    Imagine cruising through the scenic roads from {routeData?.from} to {routeData?.to}, surrounded by lush greenery, majestic mountains, and serene valleys, all from the comfort of a state-of-the-art vehicle. Our {routeData?.from} to {routeData?.to} Urbania service ensures that your journey is as enjoyable as your destination. With ample space, modern amenities, and professional drivers, we provide a seamless travel experience that allows you to relax and soak in the beauty around you.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Affordable Luxury at Your Fingertips
                  </h3>
                  <p className="text-lg">
                    At Advait Tour and Travels, we believe that luxury travel should be accessible to everyone. That's why we offer affordable urbania rental fares in {routeData?.from}, allowing you to experience the best without breaking the bank. Whether you're planning a quick weekend escape or an extended vacation, our competitive pricing ensures you get the best value for your money. Plus, with our transparent pricing policy, there are no hidden charges—just pure travel bliss.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Diverse Fleet to Meet Your Needs
                  </h3>
                  <p className="text-lg mb-4">
                    No matter the size of your travel group, we have the perfect vehicle for you. Our fleet includes a range of options to accommodate various group sizes, ensuring comfort and convenience for all. Here's a glimpse of what we offer:
                  </p>
                  <ul className="list-disc list-inside space-y-3 text-lg ml-4">
                    <li><strong>Hire 9 Seater Force Urbania in {routeData?.from}:</strong> Ideal for small groups or family outings, providing a cozy and intimate travel experience.</li>
                    <li><strong>Hire 12 Seater Force Urbania in {routeData?.from}:</strong> Perfect for mid-sized groups, offering plenty of room and amenities for a comfortable journey.</li>
                    <li><strong>Hire 16 Seater Force Urbania in {routeData?.from}:</strong> Spacious and well-equipped for large groups, perfect for corporate retreats or large family gatherings.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Premium Travel Experience
                  </h3>
                  <p className="text-lg">
                    For those who seek the pinnacle of luxury, our luxury Urbania Hire in {routeData?.from} on rent service is tailored just for you. These premium vehicles are equipped with plush interiors, advanced entertainment systems, and all the modern conveniences you need for a truly lavish travel experience. Whether it's a special occasion or you simply prefer the finer things in life, our luxury urbanias will exceed your expectations.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Flexible and Convenient Rental Options
                  </h3>
                  <p className="text-lg">
                    Planning an outstation trip? Hire our force urbania on rent in {routeData?.from} for outstation rides. Our service is designed to cater to your specific travel needs. Whether you're heading to the hills of {routeData?.to}, the spiritual sites of Himachal Pradesh, or any other destination, our vehicles are equipped to handle long journeys with ease. Enjoy the freedom to explore at your own pace with the convenience of a dedicated vehicle at your disposal.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Transparent and Competitive Pricing
                  </h3>
                  <p className="text-lg">
                    Understanding your budget is important to us. That's why we offer clear and straightforward pricing options. If you're looking for the {routeData?.from} to {routeData?.to} Urbania price or force urbania prices in {routeData?.from}, Advait Tour and Travels provide detailed quotes to help you make an informed decision. Our goal is to provide affordable rates for luxury force urbania in {routeData?.from}, ensuring that you enjoy a high-quality travel experience without any financial stress.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Easy Booking Process
                  </h3>
                  <p className="text-lg">
                    Booking your travel with Advait Tour and Travels is a breeze. Simply visit our website or give us a call to book force urbania from {routeData?.from}. Our friendly and knowledgeable staff are always ready to assist you with your queries and booking needs. We strive to make the process as smooth and hassle-free as possible, so you can focus on planning your adventure.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Why Choose Advait Tour and Travels?
                  </h3>
                  <p className="text-lg mb-4">
                    When you choose Advait Tour and Travels, you're not just booking a vehicle—you're choosing a travel partner committed to making your journey memorable. Here are a few reasons why our customers keep coming back:
                  </p>
                  <ul className="list-disc list-inside space-y-3 text-lg ml-4">
                    <li><strong>Quality and Comfort:</strong> Our Force Urbania vehicles are meticulously maintained to ensure a safe, comfortable, and enjoyable ride.</li>
                    <li><strong>Professional Drivers:</strong> Our drivers are experienced, courteous, and dedicated to providing excellent service.</li>
                    <li><strong>Customer Satisfaction:</strong> We prioritize your satisfaction and go above and beyond to meet your travel needs.</li>
                    <li><strong>Flexible Options:</strong> With a diverse fleet and various rental options, we cater to all types of travel requirements.</li>
                    <li><strong>Affordable Luxury:</strong> Enjoy the best of both worlds with our luxurious yet affordable travel solutions.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Corporate and Group Travel Solutions
                  </h3>
                  <p className="text-lg">
                    Planning a corporate retreat or a large group outing? Hire our Force Urbania in {routeData?.from} that is perfect for such occasions. These spacious vehicles can accommodate large groups comfortably, making them ideal for team-building activities, conferences, and group tours. Enjoy the journey together and arrive at your destination refreshed and ready to explore.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Customizable Your Tour
                  </h3>
                  <p className="text-lg">
                    At Advait Tour and Travels, we understand that every traveler has unique preferences and requirements. That's why we offer customizable tours tailored to your specific needs. Whether you need a one-day rental or an extended multi-day tour, we can customize it to fit your schedule and budget. Contact us to discuss your requirements and let us craft the perfect travel experience for you.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Safety First
                  </h3>
                  <p className="text-lg">
                    Your safety is our top priority. All our Force Urbania vehicles are equipped with the latest safety features and undergo regular maintenance checks to ensure they are in top condition. Our drivers are trained in safe driving practices and are committed to providing a secure travel experience. Travel with peace of mind knowing that you're in good hands with Advait Tour and Travels.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Join the Advait Tour and Travels Family
                  </h3>
                  <p className="text-lg">
                    When you choose Advait Tour and Travels, you become part of our family. We value our customers and strive to build lasting relationships based on trust and satisfaction. From the moment you book with us to the time you complete your journey, we are dedicated to providing you with exceptional service and support.
                  </p>
                </div>

                <div className="bg-blue-50 p-8 rounded-lg">
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    Book Your Force Urbania Today!
                  </h3>
                  <p className="text-lg mb-6">
                    Ready to embark on your {routeData?.to} adventure with Advait Tour and Travels? Don't wait! Book force urbania from {routeData?.from} today and experience the perfect blend of luxury, comfort, and affordability. Visit our website, call us, or drop by our office to make your reservation. We look forward to making your journey unforgettable!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* FAQs Section */}
          <div className="bg-gray-50 py-16">
            <div className="container mx-auto px-4">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                FAQs
              </h2>
              
              <div className="max-w-4xl mx-auto space-y-4">
                {faqs?.map((faq, index) =>
                <div key={index} className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <button
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors">

                      <span className="font-semibold text-gray-800 pr-4">
                        {index + 1}. {faq?.question}
                      </span>
                      <svg
                      className={`w-5 h-5 text-gray-500 transform transition-transform ${
                      openFAQ === index ? 'rotate-180' : ''}`
                      }
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">

                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openFAQ === index &&
                  <div className="px-6 pb-4 text-gray-600">
                        {faq?.answer}
                      </div>
                  }
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <ContactSection />
        </main>

        <Footer />
        <FloatingContactButtons onCallMeBackClick={() => setShowCallMeBack(true)} />
        {showCallMeBack &&
        <CallMeBackPopup isOpen={showCallMeBack} onClose={() => setShowCallMeBack(false)} />
        }
      </div>
    </>);

};

export default ForceUrbaniaRouteBooking;